<template>
  <div>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="editedItem"
        :title="
          $t('components.contacts.delete_contact_{0}', [editedItem.Email])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog
      v-model="dialogs.deactivate"
      scrollable
      persistent
      max-width="400"
    >
      <material-card
        v-if="editedItem"
        :title="
          $t('components.contacts.deactivate_contact_{0}', [editedItem.Email])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="toggle_off"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>

        <v-card-actions>
          <v-btn :disabled="saving" text @click="dialogs.deactivate = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="saving" @click="activate(false)">
            {{ $t("globals.deactivate") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog v-model="dialogs.activate" scrollable persistent max-width="400">
      <material-card
        v-if="editedItem"
        :title="
          $t('components.contacts.activate_contact_{0}', [editedItem.Email])
        "
        :color="$vuetify.theme.themes.light.success"
        icon="toggle_on"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn :disabled="saving" text @click="dialogs.activate = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="success" :loading="saving" @click="activate(true)">
            {{ $t("globals.activate") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="500px">
      <material-card :title="formTitle" :icon="formIcon" :color="formColor">
        <v-card-text>
          <v-form
            v-if="editedItem !== null && editedItem !== undefined"
            ref="form"
            v-model="valid"
          >
            <v-container fluid fill-height>
              <v-flex md12>
                <trans-select
                  :clearable="true"
                  :item="editedItem"
                  propertyName="Title"
                  :items="options.Models.Title"
                  :label="$t('headers.members_management.Title')"
                ></trans-select>
                <no-autocomplete-textfield
                  v-model="editedItem.Name"
                  :label="$t('globals.name')"
                  class="required"
                  :rules="[$rules.required]"
                ></no-autocomplete-textfield>
                <no-autocomplete-textfield
                  v-model="editedItem.Email"
                  :label="$t('headers.contacts.email')"
                  class="required"
                  :rules="[$rules.required, $rules.email]"
                ></no-autocomplete-textfield>
                <trans-select
                  :item="editedItem"
                  propertyName="Status"
                  :items="options.Models.Status"
                  :label="$t('globals.status')"
                  :rules="[$rules.required]"
                  class="required"
                  v-if="!isNew"
                ></trans-select>
                <no-autocomplete-textfield
                  v-model="editedItem.PhoneNumber"
                  :label="$t('headers.locals.phone_number')"
                  required
                  :rules="[rules.phone]"
                  v-mask="telephoneMask"
                ></no-autocomplete-textfield>
                <no-autocomplete-textfield
                  v-model="editedItem.Fax"
                  :label="$t('globals.fax_number')"
                  required
                  :rules="[rules.phone]"
                  v-mask="telephoneMask"
                ></no-autocomplete-textfield>
                <v-checkbox
                  v-model="editedItem.IsPrincipal"
                  :label="$t('components.plancreationwizard.primary')"
                ></v-checkbox>
                <trans-select2
                  v-if="entity === 'Store'"
                  v-model="editedItem"
                  propertyName="Store"
                  transPropertyName="Current"
                  :items="stores"
                  :label="$t('globals.store')"
                  :rules="[$rules.required]"
                  field="Name"
                  itemValue="Id"
                  class="required"
                ></trans-select2>
              </v-flex>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{ $t("globals.cancel") }}</v-btn>
          <v-btn
            :disabled="!valid"
            color="primary"
            :loading="saving"
            @click="save"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>

    <material-card
      :title="
        entity === 'InHouse'
          ? $t('components.plancreationwizard.internal_contacts')
          : $t('globals.contacts')
      "
      :color="$vuetify.theme.themes.light.secondary"
      icon="email"
      v-if="planOptions"
    >
      <pbas-data-table
        :exporters="$exporters"
        ref="table"
        :dataProvider="provider"
        :rowActions="rowActions"
        :floatActions="floatActions"
        fileNamePrefix="contacts"
        @itemsChanged="setData($event)"
        :identifier="identifier"
        @error="error = $event"
        :showSelect="false"
      ></pbas-data-table>
      <v-snackbar
        v-model="snackbar"
        :color="color"
        :multi-line="mode === 'multi-line'"
        :timeout="timeout"
        :vertical="mode === 'vertical'"
      >
        {{ $t(message) }}
        <v-btn dark text @click="snackbar = false">
          {{ $t("globals.close") }}
        </v-btn>
      </v-snackbar>
    </material-card>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import { ODataProvider as PbasOData } from "pbas-vue-components";
import ContactsHeaders from "@/headers/Contacts";
import TransSelect from "@/components/TransSelect";
import { locker } from "@/mixins/locker";
import { translate2 } from "@/trans2";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import TransSelect2 from "@/components/TransSelect2";
import { tokens, mask } from "vue-the-mask";
import masker from "vue-the-mask/src/masker";

export default {
  name: "contacts",
  components: {
    ErrorHandler,
    TransSelect,
    NoAutocompleteTextfield,
    TransSelect2,
  },
  mixins: [locker],
  props: [
    "entity",
    "filter",
    "relatedHeaders",
    "trustFundId",
    "identifier",
    "employerId",
  ],
  directives: { mask },
  data() {
    return {
      items: [],
      error: null,
      provider: undefined,
      saving: false,
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.editItem(false, item);
          },
          visible: (item) => !this.selectedPlan.Current.IsTesting,
        },
        {
          name: "deactivate",
          icon: "toggle_off",
          title: "globals.deactivate",
          handler: async (item) => {
            try {
              this.editedItem = await this.lock(
                this.getContact,
                {
                  id: item.Id,
                  baseUrl: this.url,
                  query: this.query,
                  dialogs: this.dialogs,
                  dialog: "deactivate",
                },
                () => {
                  this.dialogs.deactivate = false;
                }
              );
              this.dialogs.deactivate = true;
            } catch (error) {
              this.error = error;
            }
          },
          visible: (item) =>
            !this.selectedPlan.Current.IsTesting &&
            item.StatusId ===
              this.options.Models.Status.filter((o) => o.Name === "Active")[0]
                .Id,
        },
        {
          name: "activate",
          icon: "toggle_on",
          title: "globals.activate",
          handler: async (item) => {
            try {
              this.editedItem = await this.lock(
                this.getContact,
                {
                  id: item.Id,
                  baseUrl: this.url,
                  query: this.query,
                  dialogs: this.dialogs,
                  dialog: "activate",
                },
                () => {
                  this.dialogs.activate = false;
                }
              );
              this.dialogs.activate = true;
            } catch (error) {
              this.error = error;
            }
          },
          visible: (item) =>
            !this.selectedPlan.Current.IsTesting &&
            item.StatusId ===
              this.options.Models.Status.filter((o) => o.Name === "Inactive")[0]
                .Id,
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          handler: async (item) => {
            try {
              this.editedItem = await this.lock(
                this.getContact,
                {
                  id: item.Id,
                  baseUrl: this.url,
                  query: this.query,
                  dialogs: this.dialogs,
                  dialog: "delete",
                },
                () => {
                  this.dialogs.delete = false;
                }
              );
              this.dialogs.delete = true;
            } catch (error) {
              this.error = error;
            }
          },
          visible: (item) => !this.selectedPlan.Current.IsTesting,
        },
      ],
      snackbar: false,
      color: "",
      mode: "",
      timeout: 2500,
      message: "",
      e6: 1,
      dialogs: {
        edit: false,
        deactivate: false,
        activate: false,
        delete: false,
      },
      valid: true,
      headers: [],
      allHeaders: ContactsHeaders,
      editedItem: null,
      defaultItem: {
        Name: "",
        Email: "",
        IsInHouse: false,
      },
      unionLocals: [],
      stores: [],
      isNew: true,
      rules: {
        phone: (v) => {
          let val = this.unmask(v, this.telephoneMask);
          return !val || this.$rules.phone(val);
        },
      },
      telephoneMask: "(###) ### - ####",
    };
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan" }),
    ...mapGetters("plansMembers", ["options", "planOptions"]),
    floatActions() {
      if (this.selectedPlan.Current.IsTesting) {
        return [];
      }
      return [
        {
          name: "addItem",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => {
            this.editItem(true);
          },
        },
      ];
    },
    formTitle() {
      console.log(this.isNew);
      return this.isNew
        ? this.$t("components.contacts.create_new_contact")
        : this.$t("components.contacts.edit_contact_{0}", [
            this.editedItem.Email,
          ]);
    },
    formIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
    ...mapGetters("auth", ["isLoggedIn", "user", "role"]),
    ...mapGetters("common", ["language", "settings"]),
    tableTitle: function() {
      return "Plan " + translate2(this.selectedPlan, "Name") + " Contacts";
    },
    url: function() {
      return "plans/" + this.selectedPlan.Id + "/contact-info";
    },
    query: function() {
      return "?$expand=Status($expand=Translations),Store($expand=Current($expand=Translations))";
    },
  },
  watch: {
    "editedItem.Status": {
      handler(v) {
        if (this.editedItem !== null && this.editedItem !== undefined) {
          if (v) {
            this.editedItem.StatusId = this.editedItem.Status.Id;
          } else {
            this.editedItem.StatusId = null;
          }
        }
      },
    },
  },
  mounted() {
    this.loadPlanData();
  },
  methods: {
    ...mapActions("plansMembers", [
      "newContact",
      "updateContact",
      "updateContactStatus",
      "deleteContact",
      "getInsuranceCarriers",
      "getEmployers",
      "getStores",
      "getContact",
      "getCurrentUnionLocals",
      "updateUnionLocalContact",
      "newUnionLocalContact",
      "getUnionLocalContact",
      "deleteUnionLocalContact",
      "getEmployerStores",
      "getAssignableEmployerStores",
    ]),
    unmask(v, mask) {
      if (v) {
        return masker(v, mask, false, tokens);
      }
      return v;
    },
    setData(items) {
      //console.log("-----------------------set data---------------", items);
      this.items = items;
    },
    reload() {
      this.$refs.table.getDataAsync();
    },
    async loadPlanData() {
      this.defaultItem.Status = this.options.Models.Status.filter(
        (o) => o.Name === "Active"
      )[0];
      if (this.relatedHeaders) {
        this.headers = this.allHeaders.concat(this.relatedHeaders);
      } else {
        this.headers = this.allHeaders;
      }

      this.provider = new PbasOData(this.$axiosHttp, this.url, this.headers);
      if (this.filter !== null && this.filter !== undefined) {
        this.provider.filter = this.filter;
      }
    },
    validate() {
      //console.log("Validate: ", this.editedItem, this.$refs.form);
      if (this.$refs.form && this.$refs.form.validate()) {
        return true;
      }
      return false;
    },
    async editItem(isNew, item) {
      try {
        if (this.entity === "Store") {
          // if (this.planOptions.HasBillingImport) {
          //   this.stores = (await this.getAssignableEmployerStores({
          //     planId: this.selectedPlan.Id,
          //     employerId: this.employerId,
          //   })).data.value.filter((o) => o.Current.IsAssignable !== false);
          // } else {
          this.stores = (await this.getEmployerStores({
            planIdentityId: this.selectedPlan.Id,
            employerIdentityId: this.employerId,
          })).data.value;
          // }
        }
        this.isNew = isNew;
        if (this.isNew) {
          this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        } else {
          this.editedItem = await this.lock(
            this.getContact,
            {
              baseUrl: this.url,
              query: this.query,
              id: item.Id,
              dialogs: this.dialogs,
              dialog: "edit",
            },
            () => {
              this.dialogs.edit = false;
            }
          );
          // }
        }
        this.dialogs.edit = true;
      } catch (error) {
        this.error = error;
      }
    },
    close() {
      //this.unlock();
      this.isNew = true;
      this.dialogs.edit = false;
      this.editedItem = null;
      this.$refs.form.resetValidation();

      this.saving = false;
    },
    save() {
      this.editedItem.PlanIdentityId = this.selectedPlan.Id;
      let item = JSON.parse(JSON.stringify(this.editedItem));
      if (item.Status) item.StatusId = item.Status.Id;
      else item.StatusId = null;
      delete item.Status;
      switch (this.entity) {
        case "Employer": {
          item.EmployerId = this.employerId;
          break;
        }
        case "Store": {
          item.StoreId = item.Store.Id;
          delete item.Store;
          break;
        }
      }
      if (item.Title !== null && item.Title !== undefined) {
        item.TitleId = item.Title.Id;
        delete item.Title;
      }
      if (item.PhoneNumber !== null && item.PhoneNumber !== undefined) {
        item.PhoneNumber = this.unmask(item.PhoneNumber, this.telephoneMask);
      }
      if (item.Fax !== null && item.Fax !== undefined) {
        item.Fax = this.unmask(item.Fax, this.telephoneMask);
      }
      if (!this.isNew) {
        this.update(item);
      } else {
        this.add(item);
      }
    },
    async update(item) {
      this.saving = true;

      try {
        await this.updateContact({
          baseUrl: this.url,
          contact: item,
        });
        this.color = "success";
        this.message = "globals.success";
        this.snackbar = true;
        this.$refs.table.getDataAsync();
        this.close();
        this.$emit("refresh");
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
      // }
    },
    async add(item) {
      this.saving = true;
      try {
        await this.newContact({
          baseUrl: this.url,
          contact: item,
        });
        this.color = "success";
        this.message = "globals.success";
        this.snackbar = true;
        this.$refs.table.getDataAsync();
        this.$emit("refresh");
        this.close();
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },
    async activate(active) {
      this.saving = true;
      let item = JSON.parse(JSON.stringify(this.editedItem));
      if (item !== null && item !== undefined) {
        try {
          let newActive = {
            StatusId: active
              ? this.options.Models.Status.filter((o) => o.Name === "Active")[0]
                  .Id
              : this.options.Models.Status.filter(
                  (o) => o.Name === "Inactive"
                )[0].Id,
          };
          newActive.ContactIds = [item.Id];
          await this.updateContactStatus({
            baseUrl: this.url,
            payload: newActive,
          });

          this.$refs.table.getDataAsync();

          this.color = "success";
          this.message = "globals.success";
          this.snackbar = true;
          this.dialogs.deactivate = false;
          this.dialogs.activate = false;
          this.$emit("refresh");
        } catch (error) {
          this.error = error;
        }
      }
      this.saving = false;
    },
    async tryDelete() {
      this.saving = true;
      try {
        await this.deleteContact({
          baseUrl: this.url,
          contact: this.editedItem.Id,
        });
        // }

        this.color = "success";
        this.message = "globals.success";
        this.snackbar = true;
        this.dialogs.delete = false;
        this.$refs.table.getDataAsync();
        this.$emit("refresh");
      } catch (error) {
        this.error = error;
      }

      this.saving = false;
    },
  },
};
</script>
